import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { AppShell } from '@tupai/shell';
import { useAuthStore } from '@/store/auth-store';
import PhoneAuthForm from '@/components/phone-auth-form';
import { EmailAuthForm } from '@/components/email-auth-form';
import hello from '@tupai/tokens/assets/spot/hello.svg';
import BrandHeader from '@/components/brand-header';
import { useTranslation } from '@tupai/i18n/react';
import { signedInPath } from '@/lib/invite-flow';
import { IconMessageCircle, IconMail } from '@tabler/icons-react';

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    if (useAuthStore.getState().token) throw redirect({ to: signedInPath(), replace: true });
  },
  component: LoginPage,
});

// hello.svg is 270x224.
const HELLO = { src: hello, width: 110, height: 91 };

function LoginPage() {
  const [step, setStep] = useState<'picker' | 'phone' | 'otp' | 'email'>('picker');
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const sessionExpired = useAuthStore((state) => state.sessionExpired);

  return (
    <AppShell
      variant="fullscreen"
      tone="surface"
      // Fullscreen main doesn't scroll; without this the keyboard clips the CTA.
      className="relative items-center justify-center overflow-y-auto px-8 py-8"
    >
      {step === 'picker' && (
        <span className="absolute bottom-3 right-4 text-sm text-hint">
          v{import.meta.env.VITE_APP_VERSION ?? '0.0.0'}
        </span>
      )}

      {step === 'picker' && (
        <div className="flex w-full max-w-sm flex-col items-center">
          <div className="mb-10 flex flex-col items-center gap-4">
            <BrandHeader splash={HELLO} />
            <p className="font-display text-xl text-content text-center">{t('welcomeBack')}</p>
          </div>

          {sessionExpired && (
            <p role="alert" className="text-sm text-error text-center mb-4">
              {tc('errUnauthorised')}
            </p>
          )}

          <div className="flex w-full flex-col gap-3">
            <button
              onClick={() => setStep('phone')}
              className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md border-[1.5px] border-border bg-surface font-bold text-content hover:bg-bg transition-colors"
            >
              <IconMessageCircle size={20} /> {t('logInWithPhone')}
            </button>

            <button
              onClick={() => setStep('email')}
              className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md bg-primary font-bold text-surface hover:brightness-95 transition-colors"
            >
              <IconMail size={20} /> {t('logInWithEmail')}
            </button>
          </div>

          <p className="text-sm text-muted text-center mt-6">
            {t('noAccount')}{' '}
            <Link
              to="/signup"
              className="inline-flex min-h-11 items-center px-1 font-bold text-primary"
            >
              {t('signUp')}
            </Link>
          </p>
        </div>
      )}

      {(step === 'phone' || step === 'otp') && (
        <>
          {step === 'phone' && (
            <span className="absolute bottom-3 right-4 text-sm text-hint">
              v{import.meta.env.VITE_APP_VERSION ?? '0.0.0'}
            </span>
          )}

          {step === 'phone' && (
            <div className="mb-10 flex flex-col items-center gap-4">
              <BrandHeader splash={HELLO} />
              <p className="font-display text-xl text-content text-center">{t('welcomeBack')}</p>
            </div>
          )}

          <PhoneAuthForm mode="login" onStepChange={setStep} />

          {step === 'phone' && (
            <button
              onClick={() => setStep('picker')}
              className="text-sm font-bold text-muted hover:text-primary transition-colors mt-6"
            >
              {t('backToOptions')}
            </button>
          )}
        </>
      )}

      {step === 'email' && <EmailAuthForm mode="login" onBack={() => setStep('picker')} />}
    </AppShell>
  );
}
